import React from 'react';
import { useTranslation } from 'react-i18next';

import { asset } from '../../lib/asset';
import { PreviewPlaceholder } from '../wizard/PreviewPlaceholder';
import { LAPTOP_FRAME_DEFAULT, type DeviceFrameArt } from '../../steps/finish/components/DevicePreview/frame-art';
import {
  MacRoot,
  MacScreen,
  MacTopBar,
  FrameContent,
  ScaledIframe,
  FrameOverlayImg,
} from './PreviewStage.styles';

/** Render the previewed site at this virtual width, then scale to the screen. */
const RENDER_W = 1280;

export type MacDeviceProps = {
  /** Live site URL (the stable iframe's only `src`; reloads ONLY when this changes). */
  src: string;
  /** Top-nav strip + screen base tint (selected colour). */
  topColor?: string;
  /** Brand body background (the flat colour the loading placeholder fills with). */
  bgColor?: string;
  /** Bezel artwork + screen cut-out (default laptop, or the tighter finish laptop). */
  frame?: DeviceFrameArt;
  /** Bare = no bezel: the screen fills the wrapper and the iframe renders the site at
   *  the wrapper's real width (the full-screen Live Preview), not scaled from 1280. */
  bare?: boolean;
  /** Host-owned reveal gate (this iframe's `page-rendered`, else load + settle). */
  shown: boolean;
  onLoad: () => void;
  /** Same gate's ref: it identifies THIS iframe's window, so the gate can tell our
   *  `page-rendered` message apart from the phone iframe's (same origin, same URL). */
  iframeRef?: React.Ref<HTMLIFrameElement>;
};

/**
 * The persistent desktop preview device: a MacBook bezel wrapping the wizard's one
 * warm "mac-role" iframe. The iframe element is authored once and NEVER re-parented
 * — switching bezel art, going bare for full-screen, or morphing between surfaces
 * only rewrites CSS, so the desktop preview never reloads except on a real URL
 * change. Ported from the finish DevicePreview MacbookFrame.
 */
export const MacDevice = ({
  src,
  topColor,
  bgColor,
  frame = LAPTOP_FRAME_DEFAULT,
  bare = false,
  shown,
  onLoad,
  iframeRef,
}: MacDeviceProps): React.ReactElement => {
  const { t } = useTranslation();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [dims, setDims] = React.useState<{ w: number; h: number }>({ w: 0, h: 0 });

  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      setDims({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bezel: render at 1280 and scale down (shows the desktop layout in the small
  // screen). Bare: fill the wrapper at its real width (responsive full-screen view).
  const scale = bare ? 1 : dims.w > 0 ? dims.w / RENDER_W : 0;
  const width = bare ? dims.w : RENDER_W;
  const height = scale > 0 ? dims.h / scale : dims.h;

  return (
    <MacRoot>
      <MacScreen
        $bg={topColor}
        $left={frame.screen.left}
        $top={frame.screen.top}
        $width={frame.screen.width}
        $height={frame.screen.height}
        $bare={bare}
      >
        <MacTopBar $color={topColor} $bare={bare} />
        <FrameContent ref={contentRef}>
          <ScaledIframe
            ref={iframeRef}
            title={t('Desktop preview')}
            src={src || undefined}
            onLoad={onLoad}
            data-cfg-preview="mac"
            $width={width}
            $height={height}
            $scale={scale || 0.0001}
            $shown={shown && scale > 0}
          />
        </FrameContent>
        <PreviewPlaceholder shown={!shown} bgColor={bgColor} />
      </MacScreen>
      <FrameOverlayImg src={asset(frame.src)} alt="" $hidden={bare} />
    </MacRoot>
  );
};

export default MacDevice;
